// Which company the screen is showing: both, RBS alone, or SBY alone.
//
// This is a pure view filter. Every figure the landing screen needs already
// arrives split per company (CompanyAmount carries rbs/sby/group, the aging
// blocks come one per tenant, and every detail row names its company), so
// switching scope re-reads the payload rather than re-querying. That keeps the
// control instant and — more importantly — guarantees the scoped view and the
// combined view can never disagree, because they are the same numbers.

export const SCOPE_BOTH = 'BOTH';
export const SCOPE_RBS = 'RBS';
export const SCOPE_SBY = 'SBY';

export const SCOPE_OPTIONS = [
  { label: 'Both', value: SCOPE_BOTH },
  { label: 'RBS', value: SCOPE_RBS },
  { label: 'SBY', value: SCOPE_SBY },
];

const KEY = 'slb_dashboard_scope';

/** Survives a reload: the owner who only looks at RBS should not re-pick it daily. */
export const loadScope = () => {
  const stored = localStorage.getItem(KEY);
  return [SCOPE_BOTH, SCOPE_RBS, SCOPE_SBY].includes(stored) ? stored : SCOPE_BOTH;
};

export const saveScope = (scope) => localStorage.setItem(KEY, scope);

export const isBoth = (scope) => scope === SCOPE_BOTH;

/** Reads the right member of a CompanyAmount for the current scope. */
export const pick = (companyAmount, scope) => {
  if (!companyAmount) return 0;
  if (scope === SCOPE_RBS) return companyAmount.rbs;
  if (scope === SCOPE_SBY) return companyAmount.sby;
  return companyAmount.group;
};

/** Keeps rows whose `company` is in scope. */
export const filterRows = (rows, scope) =>
  isBoth(scope) ? (rows || []) : (rows || []).filter((r) => r.company === scope);

/**
 * Combines the two tenants' aging blocks down to what the current scope needs.
 * Under "Both" the buckets add; under a single company its block is returned
 * untouched, so nothing is recomputed and nothing can round differently.
 */
export const scopedAging = (rbsAging, sbyAging, scope) => {
  if (scope === SCOPE_RBS) return rbsAging;
  if (scope === SCOPE_SBY) return sbyAging;
  if (!rbsAging || !sbyAging) return rbsAging || sbyAging;

  const add = (field) => Number(rbsAging[field] ?? 0) + Number(sbyAging[field] ?? 0);
  return {
    current: add('current'),
    days31to60: add('days31to60'),
    days61to90: add('days61to90'),
    days91to120: add('days91to120'),
    daysOver120: add('daysOver120'),
    openingBalances: add('openingBalances'),
    advances: add('advances'),
    total: add('total'),
    overdue60Plus: add('overdue60Plus'),
  };
};

/** The monthly series field that matches the scope. */
export const monthValue = (point, scope) => {
  if (scope === SCOPE_RBS) return point.rbsNet;
  if (scope === SCOPE_SBY) return point.sbyNet;
  return point.groupNet;
};
